import { useEffect, useCallback, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { X, Settings, LogOut, Sun, Moon, Smartphone } from 'lucide-react'
import { useBranding } from '../../../hooks/useBranding'
import { useTheme } from '../../providers/ThemeProvider'
import { navigationGroups, settingsItem } from '../navItems'
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock'

interface MobileDrawerProps {
  open: boolean
  onClose: () => void
  onLogout: () => void
}

export function MobileDrawer({ open, onClose, onLogout }: MobileDrawerProps) {
  const { pathname } = useLocation()
  const branding = useBranding()
  const { isDark, toggleDarkMode } = useTheme()
  useBodyScrollLock(open)

  const drawerRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  const isActive = (href: string) =>
    pathname === href || (href !== '/dashboard' && pathname.startsWith(href + '/'))

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key === 'Tab' && drawerRef.current) {
        const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    },
    [onClose],
  )

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement
      document.addEventListener('keydown', handleKeyDown)
      requestAnimationFrame(() => {
        const first = drawerRef.current?.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
        first?.focus()
      })
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus()
      previousFocusRef.current = null
    }
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, handleKeyDown])

  return (
    <div
      ref={drawerRef}
      role="dialog"
      aria-modal="true"
      aria-label="Navigation menu"
      className={`fixed inset-0 z-50 transition-opacity duration-200 ${
        open ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Scrim */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      {/* Drawer panel */}
      <div
        className={`absolute inset-y-0 left-0 flex flex-col bg-surface shadow-xl transition-transform duration-200 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ width: 'min(80vw, 320px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-soft">
          <div className="flex items-center gap-2">
            <img src={branding.icon} alt={branding.name} className="h-8 w-8 rounded-lg" />
            <span className="text-lg font-bold bg-brand bg-clip-text text-transparent">
              {branding.name}
            </span>
          </div>
          {/* 2026-08-29: every row and control in here sat under the 44px
              touch floor — nav rows at 36px, the theme toggle at 28px. */}
          <button
            onClick={onClose}
            aria-label="Close navigation menu"
            className="inline-flex items-center justify-center min-h-touch min-w-touch rounded-lg hover:bg-surface-alt"
          >
            <X className="h-5 w-5 text-muted" />
          </button>
        </div>

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
          {navigationGroups.map((group) => (
            <div key={group.name}>
              <p className="px-3 mb-1 text-app-micro font-semibold uppercase tracking-wider text-muted">
                {group.name}
              </p>
              {group.items.map((item) => {
                const effectiveHref = item.href
                const active = isActive(effectiveHref) ||
                  (item.href === '/schedule' && (isActive('/schedule') || isActive('/schedule-v2')))
                return (
                  <Link
                    key={item.href}
                    to={effectiveHref}
                    onClick={onClose}
                    className={`flex items-center gap-3 px-3 py-2 min-h-touch text-sm font-medium rounded-lg transition-colors ${
                      active
                        ? 'bg-brand text-on-brand'
                        : 'text-secondary hover:bg-surface-alt'
                    }`}
                  >
                    <item.icon className="h-4 w-4 flex-shrink-0" />
                    {item.name}
                  </Link>
                )
              })}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-soft px-3 py-3 space-y-1">
          {/* Theme controls */}
          <div className="flex items-center gap-2 px-3 py-2">
            <button
              onClick={toggleDarkMode}
              className="inline-flex items-center gap-1.5 text-xs px-3 min-h-touch rounded-md bg-surface-alt text-secondary"
            >
              {isDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
              {isDark ? 'Light' : 'Dark'}
            </button>
          </div>

          <Link
            to={settingsItem.href}
            onClick={onClose}
            className="flex items-center gap-3 px-3 py-2 min-h-touch text-sm font-medium rounded-lg text-secondary hover:bg-surface-alt"
          >
            <Settings className="h-4 w-4" />
            Settings
          </Link>

          <Link
            to="/download"
            onClick={onClose}
            className="flex items-center gap-3 px-3 py-2 min-h-touch text-sm font-medium rounded-lg text-secondary hover:bg-surface-alt"
          >
            <Smartphone className="h-4 w-4" />
            Get the App
          </Link>

          <button
            onClick={() => { onClose(); onLogout() }}
            className="w-full flex items-center gap-3 px-3 py-2 min-h-touch text-sm font-medium rounded-lg text-bad hover:bg-bad/10"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </div>
    </div>
  )
}
